import React from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { tournamentsApi } from '@/api/tournaments'
import { lineupsApi } from '@/api/lineups'
import { Button } from './ui/Button'

export const PendingTournamentBanner: React.FC = () => {
  const { user } = useAuth()

  // Get all tournaments for user's default league (don't filter by status on API)
  const defaultLeagueId = user?.leagues?.[0]

  const { data: allTournaments } = useQuery({
    queryKey: ['tournaments', defaultLeagueId],
    queryFn: () => tournamentsApi.getTournaments(defaultLeagueId),
    enabled: !!defaultLeagueId,
  })

  // Filter to only pending tournaments client-side
  const tournaments = allTournaments?.filter(
    (t) => t.currentRound === 'pending' || t.status === 'pending'
  )

  // Get user's lineups to check which tournaments they've already entered
  const { data: myLineups } = useQuery({
    queryKey: ['myLineups'],
    queryFn: () => lineupsApi.getAllMyLineups(),
  })

  if (!tournaments || tournaments.length === 0) {
    return null
  }

  // Filter tournaments that are still accepting entries
  const now = new Date()
  const availableTournaments = tournaments.filter((tournament) => {
    const deadline = new Date(tournament.entryDeadline)
    return deadline > now
  })

  if (availableTournaments.length === 0) {
    return null
  }

  // Get the next upcoming tournament (earliest deadline)
  const nextTournament = availableTournaments.sort(
    (a, b) => new Date(a.entryDeadline).getTime() - new Date(b.entryDeadline).getTime()
  )[0]

  // Check if user already has a lineup for this tournament
  const hasLineup = myLineups?.some((lineup) => lineup.tourneyId === nextTournament.id)

  // Format the deadline
  const deadlineDate = new Date(nextTournament.entryDeadline)
  const formattedDeadline = deadlineDate.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  return (
    <div className="bg-gradient-to-r from-gold-500 to-gold-600 dark:from-gold-700 dark:to-gold-800 text-white shadow-md">
      <div className="container-custom py-3">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex-1 text-center sm:text-left">
            <p className="font-semibold text-lg font-serif">
              {hasLineup ? '✏️ Edit Your Picks' : '🏌️ Make Your Picks'}
            </p>
            <p className="text-sm text-gold-50 dark:text-gold-200 font-sans">
              <span className="font-medium">{nextTournament.name}</span> • Deadline:{' '}
              {formattedDeadline}
            </p>
          </div>
          <Link to={`/tournaments/${nextTournament.id}/pick`}>
            <Button
              variant="primary"
              className="bg-white text-gold-700 hover:bg-cream-100 dark:bg-charcoal-800 dark:text-gold-300 dark:hover:bg-charcoal-700 font-semibold shadow-sm"
            >
              {hasLineup ? 'Edit Lineup' : 'Make Picks Now'}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}
